import type { SettingsTabSection } from '@xhubio-saas/core-frontend/kernel'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { SettingsNav } from './SettingsNav'

interface SettingsLayoutProps {
  children: ReactNode
  /**
   * Welche Seite: `'account'` = „Mein Konto" (Avatar-Menue, `/settings`),
   * `'system'` = „System" der Organisation (Seitenleiste, `/system`).
   * Default: `'account'`.
   */
  section?: SettingsTabSection
}

/**
 * Settings layout.
 *
 * Desktop (lg+): an inner sidebar on the left (SettingsNav's vertical variant)
 * and the page content beside it — settings pages get the full remaining
 * width. Below lg the nav renders as a horizontally scrollable tab strip above
 * the content. `minmax(0, 1fr)` on the content column so wide content (the
 * System page's side-by-side cards) shrinks instead of blowing out the grid.
 */
export function SettingsLayout({ children, section = 'account' }: SettingsLayoutProps) {
  const { t } = useTranslation()
  const title =
    section === 'system' ? t('settings.system.tab', 'System') : t('common.myAccount', 'My account')

  return (
    <div className="container mx-auto py-8 px-4 space-y-6">
      <div>
        {/* -0.04em is the app's display tracking (see DESIGN.md / authUi) —
            tracking-tight (-0.025em) was the one heading off the system. */}
        <h1 className="text-3xl tracking-[-0.04em]">{title}</h1>
      </div>

      <div className="lg:grid lg:grid-cols-[12rem_minmax(0,1fr)] lg:gap-10">
        <div className="mb-6 lg:mb-0">
          <SettingsNav section={section} />
        </div>
        <div>{children}</div>
      </div>
    </div>
  )
}
